import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import { AlertCircle } from "lucide-react";
import React from "react";
import TooltipLink from "./TooltipLink";
import Link from "next/link";

interface RedAlertProps {
  title?: string;
  className?: string;
  description?: string;
  color?: string;
}

const RedAlert: React.FC<RedAlertProps> = ({
  title = "표를 좌우로 스와이프 하세요",
  className,
  description,
  color,
}) => {
  const processMarkdown = (
    text: string,
    isTitle?: boolean
  ): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const markdownRegex =
      /\[[^\]]+\]\(<[^>]+>\)|\[[^\]]+\]\([^)]+\)|\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    if (!text) return text;

    // Reset lastIndex to test if any matches are found.
    markdownRegex.lastIndex = 0;
    if (!markdownRegex.test(text)) {
      return text;
    }
    // Since .test() advances lastIndex, reset it again before the exec loop.
    markdownRegex.lastIndex = 0;

    while ((match = markdownRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const matchedText = match[0];

      if (match[1] !== undefined) {
        // Bold text match: **content**
        const boldContent = match[1];
        parts.push(
          <strong key={`bold-${keyIndex++}`}>
            {processMarkdown(boldContent, isTitle)}
          </strong>
        );
      } else {
        // Link match
        let linkText: string;
        let href: string;

        if (matchedText.includes("](<") && matchedText.endsWith(">)")) {
          const linkMatch = matchedText.match(/^\[([^\]]+)\]\(<([^>]+)>\)$/);
          if (linkMatch) {
            [, linkText, href] = linkMatch;
          } else {
            continue;
          }
        } else {
          const linkMatch = matchedText.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          if (linkMatch) {
            [, linkText, href] = linkMatch;
          } else {
            continue;
          }
        }

        const processedLinkText = processMarkdown(linkText, isTitle);

        if (href.startsWith("/") || href.startsWith("#")) {
          if (typeof processedLinkText === "string") {
            parts.push(
              <TooltipLink
                key={`link-${keyIndex++}`}
                href={href}
                text={processedLinkText}
                IconClassName="size-3"
                TextClassName={isTitle ? "text-base" : "text-sm"}
                LinkClassName="inline-flex"
                ButtonClassName="!p-0 !h-auto !gap-1 !items-baseline !mr-1 text-current"
              />
            );
          } else {
            // TooltipLink's text prop is a string, so we use a regular Link for rich content.
            parts.push(
              <Link
                key={`link-${keyIndex++}`}
                href={href}
                className="inline-flex items-baseline gap-1 mr-1 underline"
              >
                {processedLinkText}
              </Link>
            );
          }
        } else {
          parts.push(
            <a
              key={`link-${keyIndex++}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {processedLinkText}
            </a>
          );
        }
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? <span>{parts}</span> : text;
  };

  // 색상 매핑 객체 (다크모드 대응)
  const colorMap: Record<string, { icon: string; text: string }> = {
    amber: {
      icon: "!text-amber-500 dark:!text-amber-400",
      text: "!text-amber-600 dark:!text-amber-300",
    },
    orange: {
      icon: "!text-orange-500 dark:!text-orange-400",
      text: "!text-orange-600 dark:!text-orange-300",
    },
    yellow: {
      icon: "!text-yellow-500 dark:!text-yellow-400",
      text: "!text-yellow-600 dark:!text-yellow-300",
    },
    green: {
      icon: "!text-green-500 dark:!text-green-400",
      text: "!text-green-600 dark:!text-green-300",
    },
    blue: {
      icon: "!text-blue-500 dark:!text-blue-400",
      text: "!text-blue-600 dark:!text-blue-300",
    },
    purple: {
      icon: "!text-purple-500 dark:!text-purple-400",
      text: "!text-purple-600 dark:!text-purple-300",
    },
    pink: {
      icon: "!text-pink-500 dark:!text-pink-400",
      text: "!text-pink-600 dark:!text-pink-300",
    },
    indigo: {
      icon: "!text-indigo-500 dark:!text-indigo-400",
      text: "!text-indigo-600 dark:!text-indigo-300",
    },
    stone: {
      icon: "!text-stone-500 dark:!text-stone-400",
      text: "!text-stone-600 dark:!text-stone-300",
    },
    gray: {
      icon: "!text-gray-500 dark:!text-gray-400",
      text: "!text-gray-600 dark:!text-gray-300",
    },
  };

  const colorClasses = color ? colorMap[color] : undefined;

  return (
    <Alert
      variant="destructive"
      className={`flex flex-wrap items-center mb-2 ${className || ""}`}
    >
      <AlertCircle className={`w-4 h-4 ${colorClasses?.icon || ""}`} />
      {/* prettier-ignore */}
      <AlertTitle className={`text-base flex-1 min-w-0 ${colorClasses?.text || ""}`}>{processMarkdown(title, true)}</AlertTitle>
      <AlertDescription
        className={`basis-full ml-7 whitespace-pre-line ${colorClasses?.text || ""}`}
      >
        {description ? processMarkdown(description) : description}
      </AlertDescription>
    </Alert>
  );
};

export default RedAlert;
